import {buildMedicationLogRows} from './utils/medicationLogRows';
import {
  getCanonicalEntryDisplayInfo,
  isBehaviorIncidentEntry,
  LOG_TYPES,
  getCaregiverPalette,
} from '../../constants/logTypeRegistry';
import { getTimelineSourceMeta } from '../../constants/timelineSourceRegistry';
import { getRoleDisplay } from '../../constants/roles';
import {
  ACTIVITY_TYPE_OPTIONS,
  getActivityTheme,
  getPrimaryActivityTheme,
} from '../../constants/activityThemes';
import { coerceCalendarDate } from '../../utils/calendarDateKey';
import {
  getTimelineEntryBehaviorType,
  getTimelineEntryEditTarget,
  isSmartCaptureObservationEntry,
} from '../../utils/timelineEntryBehavior';
import { resolvePreviewTitle } from '../Dashboard/ImportLogs/importLogsReviewUtils';
import { buildCaptureFirstTimelineDetails } from '../../features/rawCareCaptureExtraction/captureFirstTimelineDetails';
import { resolveCaptureFirstDisplayTitle } from './captureFirstDisplayTitle';
import { resolveTitle } from '../../shared/sourceTitleResolver';
import { resolveAdaptiveResponsePresentation } from '../Dashboard/shared/adaptiveQuickLogConfig';
import { resolveAdaptiveFoodLogPresentation } from '../Dashboard/shared/adaptiveFoodLogConfig';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { resolveAdaptiveBathroomLogPresentation } from '../Dashboard/shared/adaptiveBathroomLogConfig';

const normalizeText = (value) => String(value || '').trim();

const isImportSourceEntry = (entry = {}) => (
  entry?.source === 'import'
  || entry?.source?.type === 'import'
);

const getImportedNarrativeText = (entry = {}) => normalizeText(
  entry.note
  || entry.notes
  || entry.sourceText
  || entry.originalText
  || entry.content
  || entry.text
  || entry.description
  || ''
);

const isDocumentPointerEntry = (entry = {}) => {
  const pointerType = normalizeText(entry.timelineType || entry.type || entry.category || entry.pointerKind || '').toLowerCase();
  return pointerType === 'document_pointer'
    || pointerType === 'document_source_pointer'
    || entry.pointerKind === 'document_timeline'
    || entry.pointerKind === 'document_source';
};

const isStoryMomentReferenceEntry = (entry = {}) => {
  const pointerType = normalizeText(entry.timelineType || entry.type || entry.category || entry.pointerKind || '').toLowerCase();
  return pointerType === 'story_moment_reference'
    || entry.kind === 'story_moment_reference'
    || entry.pointerKind === 'story_moment_reference';
};

const isDoctorVisitEntry = (entry = {}) => {
  const entryType = normalizeText(entry.timelineType || entry.entryType || entry.type || entry.category || '').toLowerCase();
  return entryType === 'doctor_visit';
};

const getDocumentPointerSubtype = (entry = {}) => (
  normalizeText(entry.timelineType || entry.type || entry.category || '').toLowerCase() === 'document_source_pointer'
  || entry.pointerKind === 'document_source'
    ? 'document_source_pointer'
    : 'document_pointer'
);

const buildStoryMomentReferencePresentation = (entry = {}) => {
  const sourceTitle = normalizeText(
    entry.storyParentLabel
    || entry.storyDisplayLabel
    || entry.sourceStoryLabel
    || entry.displayTitle
    || entry.title
    || 'Source story'
  );
  const momentTitle = normalizeText(
    entry.momentTitle
    || entry.sceneTitle
    || entry.title
    || entry.titlePrefix
    || entry.body
    || entry.summary
    || ''
  );
  const storyCaptureDate = entry.storyCaptureTimestamp || entry.storyLoggedAt || entry.storyCreatedAt || null;
  const title = momentTitle || sourceTitle || 'Moment';
  const bodyText = normalizeText(
    entry.momentBody
    || entry.momentText
    || entry.storyMomentText
    || entry.sourceSpanText
    || entry.sourceText
    || entry.body
    || entry.text
    || entry.summary
    || entry.note
    || entry.description
    || ''
  );
  return {
    kind: 'story_moment_reference',
    label: 'Story moment',
    displayTitle: title,
    bodyText,
    notesText: '',
    primaryText: momentTitle || sourceTitle,
    triggerSummary: '',
    categoryLabel: 'Story moment',
    icon: null,
    iconComponent: HubOutlinedIcon,
    sourceMeta: {
      type: 'story_moment_reference',
      label: 'Story moment',
      body: bodyText,
      receivedAt: storyCaptureDate || entry.loggedAt || entry.createdAt || entry.timestamp || null,
    },
    sourcePresentation: {
      type: 'story_moment_reference',
      label: 'Story moment',
      isEmail: false,
      isParsed: false,
      needsReview: false,
      isManual: false,
    },
    navigationTarget: entry.navigationTarget || (entry.sourceNoteId ? `/sources/${entry.sourceNoteId}?momentId=${encodeURIComponent(entry.storyMomentId || entry.momentId || entry.id || '')}` : ''),
    sourceId: entry.sourceId || entry.sourceNoteId || '',
    sourceNoteId: entry.sourceNoteId || entry.sourceId || '',
    storyMomentId: entry.storyMomentId || entry.momentId || entry.id || '',
    pointerKey: entry.pointerKey || '',
    count: 1,
    candidateRefs: [],
  };
};

const buildDocumentPointerPresentation = (entry = {}) => {
  const pointerSubtype = getDocumentPointerSubtype(entry);
  const sourceTitle = normalizeText(
    entry.sourceTitle
    || entry.title
    || entry.displayTitle
    || entry.sourceMeta?.label
    || 'Document source'
  );
  const count = Number(entry.count || 0) || 1;
  const occurredOnDate = normalizeText(entry.occurredOnDate || entry.localDate || entry.entryDayKey || '');
  const documentPurposeLabel = normalizeText(entry.documentPurpose?.label || entry.documentPurpose?.type || '');
  const documentTypeLabel = normalizeText(entry.documentType || 'Uploaded document');
  const summary = pointerSubtype === 'document_source_pointer'
    ? normalizeText(entry.body || entry.summary || 'Document added.')
    : `${count} extracted timeline item${count === 1 ? '' : 's'}`;
  const details = pointerSubtype === 'document_source_pointer'
    ? normalizeText(entry.notes || entry.description || entry.documentFindingSummary?.notes || '') || [documentPurposeLabel, occurredOnDate].filter(Boolean).join(' · ')
    : [documentTypeLabel, documentPurposeLabel, occurredOnDate].filter(Boolean).join(' · ');
  const label = pointerSubtype === 'document_source_pointer' ? 'Document' : 'Document source';
  const categoryLabel = pointerSubtype === 'document_source_pointer'
    ? (documentPurposeLabel || 'Document')
    : 'Document source';
  const sourceMetaBody = pointerSubtype === 'document_source_pointer'
    ? details
    : [documentTypeLabel, documentPurposeLabel, occurredOnDate].filter(Boolean).join(' · ');

  return {
    kind: pointerSubtype,
    label,
    displayTitle: sourceTitle || label,
    bodyText: summary,
    notesText: details,
    primaryText: summary,
    categoryLabel,
    icon: '📄',
    iconComponent: null,
    sourceMeta: {
      type: pointerSubtype,
      label,
      body: sourceMetaBody,
      receivedAt: entry.loggedOnDate || entry.createdAt || null,
    },
    sourcePresentation: {
      type: pointerSubtype,
      label,
      isEmail: false,
      isParsed: false,
      needsReview: false,
      isManual: false,
    },
    navigationTarget: entry.navigationTarget || (entry.sourceId ? `/sources/${entry.sourceId}` : ''),
    sourceId: entry.sourceId || '',
    pointerKey: entry.pointerKey || '',
    count,
    candidateRefs: Array.isArray(entry.candidateRefs) ? entry.candidateRefs : [],
  };
};

const condenseRepeatedText = (value = '') => {
  let text = normalizeText(value);
  if (!text) {
    return '';
  }

  let previous = '';
  while (text !== previous) {
    previous = text;
    text = text.replace(/\b([\w'’-]+)(?:\s+\1\b)+/gi, '$1');
  }

  return text.replace(/\s+/g, ' ').trim();
};

const formatSleepDateTime = (value) => {
  const date = coerceCalendarDate(value);
  if (!date) {
    return '';
  }

  const dateLabel = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeLabel = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${dateLabel} ${timeLabel}`;
};

const formatDoctorVisitDateTime = (entry = {}) => {
  const date = coerceCalendarDate(entry.visitDateTime || entry.visitAt || entry.timestamp || entry.visitDate || entry.createdAt);
  if (!date) {
    return normalizeText(entry.visitDate || entry.visitDateTime || entry.visitAt || '');
  }

  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getDoctorVisitDetails = (entry = {}) => {
  const rows = [];
  const providerLabel = normalizeText(
    entry.providerName
    || entry.doctorName
    || entry.providerSnapshot?.doctorName
    || ''
  );
  const providerSpecialty = normalizeText(
    entry.providerSpecialty
    || entry.specialty
    || entry.providerSnapshot?.specialty
    || ''
  );
  const reasonForVisit = normalizeText(entry.reasonForVisit);
  const diagnosis = normalizeText(entry.diagnosis);
  const treatmentRecommendations = normalizeText(entry.treatmentRecommendations);
  const followUpDate = normalizeText(entry.followUpDate);
  const summaryNotes = normalizeText(entry.summaryNotes);
  const visitDateTime = formatDoctorVisitDateTime(entry);

  if (visitDateTime) {
    rows.push({ label: 'Visit', value: visitDateTime });
  }

  if (providerLabel) {
    rows.push({ label: 'Provider', value: [providerLabel, providerSpecialty].filter(Boolean).join(' · ') });
  }

  if (reasonForVisit) {
    rows.push({ label: 'Reason', value: reasonForVisit });
  }

  if (diagnosis) {
    rows.push({ label: 'Diagnosis', value: diagnosis });
  }

  if (treatmentRecommendations) {
    rows.push({ label: 'Treatment', value: treatmentRecommendations });
  }

  if (followUpDate) {
    rows.push({ label: 'Follow-up', value: followUpDate });
  }

  if (summaryNotes) {
    rows.push({ label: 'Summary', value: summaryNotes });
  }

  return rows;
};

const formatSleepDuration = (durationMin) => {
  const minutes = Number(durationMin);
  if (Number.isNaN(minutes) || minutes < 0) {
    return '';
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours <= 0) {
    return `${remainder}m`;
  }

  return `${hours}h ${remainder}m`;
};

const getInitials = (name) => {
  if (!name) {
    return null;
  }

  const parts = String(name)
    .split(/[\s@._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return null;
  }

  if (parts.length === 1) {
    return parts[0]
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase();
  }

  const first = parts[0][0] || '';
  const last = parts[parts.length - 1][0] || '';

  return `${first}${last}`.toUpperCase().slice(0, 2);
};

const getSemanticSeverityMeta = (severity) => {
  if (severity == null) {
    return null;
  }

  const numericValue = Number(severity);
  if (Number.isNaN(numericValue)) {
    return null;
  }

  if (numericValue >= 9) {
    return {
      value: numericValue,
      label: 'Critical',
      displayLabel: `Critical (${numericValue}/10)`,
      color: '#B42318',
      textColor: '#9F1239',
      bg: '#FEF2F2',
      border: '#FECACA',
    };
  }

  if (numericValue >= 6) {
    return {
      value: numericValue,
      label: 'High',
      displayLabel: `High (${numericValue}/10)`,
      color: '#C2410C',
      textColor: '#9A3412',
      bg: '#FFF7ED',
      border: '#FDBA74',
    };
  }

  if (numericValue >= 3) {
    return {
      value: numericValue,
      label: 'Moderate',
      displayLabel: `Moderate (${numericValue}/10)`,
      color: '#B45309',
      textColor: '#92400E',
      bg: '#FFFBEB',
      border: '#FCD34D',
    };
  }

  return {
    value: numericValue,
    label: 'Low',
    displayLabel: `Low (${numericValue}/10)`,
    color: '#047857',
    textColor: '#065F46',
    bg: '#ECFDF3',
    border: '#A7F3D0',
  };
};

const getEngagementMeta = (value) => {
  const metaMap = {
    1: { label: 'Low', color: '#64748B', bg: '#FFFFFF', textColor: '#334155' },
    2: { label: 'Okay', color: '#64748B', bg: '#FFFFFF', textColor: '#334155' },
    3: { label: 'Good', color: '#6366F1', bg: '#EEF2FF', textColor: '#312E81' },
    4: { label: 'Great', color: '#4F46E5', bg: '#EEF2FF', textColor: '#312E81' },
    5: { label: 'Fully engaged', color: '#4338CA', bg: '#E0E7FF', textColor: '#1E1B4B' },
  };

  return metaMap[Number(value)] || null;
};

const getBehaviorContextSignals = (contextSnapshot = {}) => {
  const signals = [
    {
      icon: '💊',
      label: 'Meds',
      present: Array.isArray(contextSnapshot.medicationsTaken)
        ? contextSnapshot.medicationsTaken.some((item) => item && item !== 'Not logged yet')
        : Boolean(contextSnapshot.medicationsTaken && contextSnapshot.medicationsTaken !== 'Not logged yet'),
    },
    {
      icon: '🍽️',
      label: 'Food',
      present: Boolean(contextSnapshot.foodLogged && contextSnapshot.foodLogged !== 'Not logged yet'),
    },
    {
      icon: '🏃',
      label: 'Activity',
      present: Array.isArray(contextSnapshot.activities)
        ? contextSnapshot.activities.some((item) => item && item !== 'Not logged yet')
        : Boolean(contextSnapshot.activities && contextSnapshot.activities !== 'Not logged yet'),
    },
    {
      icon: '😴',
      label: 'Sleep',
      present: Boolean(contextSnapshot.sleepQuality && contextSnapshot.sleepQuality !== 'Not logged yet'),
    },
  ];

  return signals.filter((signal) => signal.present);
};

const buildContextSignalsFromSnapshot = (contextSnapshot = {}) => (
  getBehaviorContextSignals(contextSnapshot).map((signal) => ({
    icon: signal.icon,
    label: signal.label,
    present: true,
  }))
);

const normalizeActivityValue = (entry = {}) => {
  const candidates = [
    entry.activityDetails?.activityThemeLabel,
    entry.activityDetails?.activityType,
    Array.isArray(entry.activityDetails?.activityTypes) ? entry.activityDetails.activityTypes[0] : entry.activityDetails?.activityTypes,
    entry.activityThemeLabel,
    entry.data?.activityThemeLabel,
    Array.isArray(entry.activityTypes) ? entry.activityTypes[0] : entry.activityTypes,
    Array.isArray(entry.data?.activityTypes) ? entry.data.activityTypes[0] : entry.data?.activityTypes,
    entry.activityType,
    entry.data?.activityType,
    entry.categoryLabel,
    entry.data?.categoryLabel,
    entry.titlePrefix,
  ];

  return candidates.find((candidate) => normalizeText(candidate)) || '';
};

const resolveActivityMeta = (entry = {}) => {
  const activityLabel = normalizeActivityValue(entry);
  const normalizedValue = normalizeText(activityLabel).toLowerCase();
  const matchedOption = ACTIVITY_TYPE_OPTIONS.find((option) => {
    const optionValue = normalizeText(option.value).toLowerCase();
    const optionLabel = normalizeText(option.label).toLowerCase();
    return normalizedValue === optionValue || normalizedValue === optionLabel;
  });
  const theme = matchedOption
    ? getActivityTheme(matchedOption.value)
    : getPrimaryActivityTheme(
        entry.activityDetails?.activityTypes
        || entry.activityDetails?.activityType
        || entry.activityTypes
        || entry.data?.activityTypes
        || normalizeText(activityLabel).toLowerCase()
        || 'other'
      );

  return {
    label: matchedOption?.label || theme.label || activityLabel || 'Activity',
    icon: matchedOption?.iconComponent ? null : (matchedOption?.emoji || LOG_TYPES.activity.icon),
    iconComponent: matchedOption?.iconComponent || LOG_TYPES.activity.iconComponent || null,
    color: entry.activityDetails?.activityThemeColor || entry.activityThemeColor || entry.data?.activityThemeColor || theme.color || LOG_TYPES.activity.palette.dot,
    softBg: theme.softBg,
    dark: theme.dark,
    border: theme.border,
    key: entry.activityDetails?.activityThemeKey || entry.activityThemeKey || entry.data?.activityThemeKey || theme.key,
  };
};

const getFoodMealType = (entry = {}) => (
  entry.foodDetails?.mealType
  || ''
);

const getFoodHeadline = (entry = {}) => (
  entry.foodDetails?.whatWasEaten
  || ''
);

const getFoodReactionValue = (entry = {}) => {
  const foodDetails = entry.foodDetails || {};
  const candidates = [
    foodDetails.reaction,
  ];

  return candidates.find((value) => normalizeText(value)) || '';
};

const getFoodRequiresReview = (entry = {}) => {
  const foodDetails = entry.foodDetails || {};
  const reaction = normalizeText(getFoodReactionValue(entry)).toLowerCase();
  return Boolean(
    foodDetails.requiresReview
    || foodDetails.reviewReason === 'allergy_concern'
    || reaction === 'allergy concern'
  );
};

const getFoodTextHeadline = (value = '') => {
  const text = normalizeText(value);
  if (!text) {
    return '';
  }

  return text
    .replace(/\s+[—-]\s+Portion:\s*[^—-]+/gi, '')
    .replace(/\s+[—-]\s+Reaction:\s*[^—-]+/gi, '')
    .replace(/\s+[—-]\s+Notes:\s*[^—-]+/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getFoodDetails = (entry = {}, adaptivePresentation = null) => {
  const foodDetails = entry.foodDetails || {};
  const rows = [];
  const reaction = getFoodReactionValue(entry);
  const requiresReview = getFoodRequiresReview(entry);
  const amountLabel = normalizeText(foodDetails.adaptiveAmountLabel || adaptivePresentation?.amountLabel || 'Amount');
  const reactionLabel = normalizeText(foodDetails.adaptiveReactionLabel || adaptivePresentation?.reactionLabel || 'Reaction');
  const notesLabel = normalizeText(foodDetails.adaptiveNotesLabel || adaptivePresentation?.notesLabel || 'Notes');

  if (foodDetails.portion) {
    rows.push({ label: amountLabel, value: String(foodDetails.portion) });
  }

  if (reaction && reaction !== 'None') {
    rows.push({ label: reactionLabel, value: String(reaction) });
  }

  if (requiresReview) {
    rows.push({
      label: 'Review',
      value: 'Allergy concern',
      valueColor: '#B42318',
    });
  }

  const foodNotes = foodDetails.notes;
  if (foodNotes) {
    rows.push({ label: notesLabel, value: String(foodNotes) });
  }

  return rows.filter(Boolean);
};

const hasRichImportedFoodStructure = (entry = {}) => {
  const foodDetails = entry.foodDetails || {};
  return Boolean(
    normalizeText(foodDetails.mealType)
    || normalizeText(foodDetails.portion)
    || normalizeText(foodDetails.reaction)
    || normalizeText(foodDetails.notes)
    || foodDetails.requiresReview
    || normalizeText(foodDetails.reviewReason)
  );
};

const getFoodReviewFlag = (entry = {}) => {
  return getFoodRequiresReview(entry);
};

const BRISTOL_SCALE_META = {
  1: { label: 'Type 1', description: 'Separate hard lumps — Severe constipation' },
  2: { label: 'Type 2', description: 'Sausage-shaped, lumpy — Constipation' },
  3: { label: 'Type 3', description: 'Sausage with cracks — Normal range' },
  4: { label: 'Type 4', description: 'Smooth, soft sausage — Ideal' },
  5: { label: 'Type 5', description: 'Soft blobs, clear edges — Normal range' },
  6: { label: 'Type 6', description: 'Fluffy pieces, ragged — Mild diarrhea' },
  7: { label: 'Type 7', description: 'Watery, no solid — Diarrhea' },
};

const getBristolMeta = (value) => {
  const numeric = Number(value);
  return BRISTOL_SCALE_META[numeric] || null;
};

const getToiletSummary = (entry = {}) => {
  const bathroomDetails = entry.bathroomDetails || entry.data?.bathroomDetails || {};
  const type = normalizeText(bathroomDetails.type || entry.toiletType || '');
  const location = normalizeText(bathroomDetails.location || entry.location || '');
  const consistencyMeta = getBristolMeta(bathroomDetails.consistency || entry.consistency);
  const narrativeText = normalizeText(
    bathroomDetails.notes
    || entry.notes
    || entry.description
    || entry.summary
    || entry.content
    || entry.text
    || entry.title
    || ''
  );

  if (type) {
    return type;
  }

  if (consistencyMeta) {
    return consistencyMeta.label;
  }

  if (location) {
    return location;
  }

  return narrativeText;
};

const getSleepDetails = (entry = {}) => {
  const sleepDetails = entry.sleepDetails || entry.data?.sleepDetails || {};
  const rows = [];
  const disturbanceLabels = {
    none: 'Slept through',
    once: 'Woke once',
    multiple: 'Woke multiple',
    early: 'Up too early',
    late: 'Up too late',
  };

  if (sleepDetails.quality || entry.sleepQuality) {
    rows.push({ label: 'Quality', value: String(sleepDetails.quality || entry.sleepQuality) });
  }

  if (sleepDetails.disturbances || entry.disturbance) {
    const disturbanceValue = String(sleepDetails.disturbances || entry.disturbance || '').trim();
    rows.push({
      label: 'Disturbance',
      value: disturbanceLabels[disturbanceValue] || disturbanceValue,
    });
  }

  if (sleepDetails.notes || entry.notes) {
    rows.push({ label: 'Notes', value: String(sleepDetails.notes || entry.notes) });
  }

  if (sleepDetails.status || entry.status) {
    rows.push({ label: 'Status', value: String(sleepDetails.status || entry.status) });
  }

  return rows;
};

const hasFullStructuredSleepPresentation = (entry = {}) => {
  const sleepDetails = entry.sleepDetails || entry.data?.sleepDetails || {};
  return Boolean(
    sleepDetails.startAt
    || sleepDetails.startTime
    || sleepDetails.bedtime
    || sleepDetails.endAt
    || sleepDetails.endTime
    || sleepDetails.wakeTime
    || sleepDetails.durationMin != null
    || sleepDetails.durationHours != null
    || entry.durationMin != null
    || entry.startAt
    || entry.endAt
    || entry.bedtime
    || entry.wakeTime
  );
};

const isQuickLogSleepNoteEntry = (entry = {}) => {
  const timestampSource = normalizeText(entry.timestampSource || entry.data?.timestampSource || '').toLowerCase();
  return (
    entry.category === 'sleep'
    && (
      timestampSource === 'quick-note'
      || timestampSource === 'local-input'
    )
  );
};

const getSleepHeadline = (entry = {}) => {
  const sleepDetails = entry.sleepDetails || entry.data?.sleepDetails || {};
  const bedtime = formatSleepDateTime(
    sleepDetails.startAt
    || sleepDetails.startTime
    || sleepDetails.bedtime
    || entry.startAt
    || entry.startTime
    || entry.bedtime
  );
  const wakeTime = formatSleepDateTime(
    sleepDetails.endAt
    || sleepDetails.endTime
    || sleepDetails.wakeTime
    || entry.endAt
    || entry.endTime
    || entry.wakeTime
  );
  const status = normalizeText(sleepDetails.status || entry.status).toLowerCase();
  const durationMin = sleepDetails.durationMin != null
    ? Number(sleepDetails.durationMin)
    : entry.durationMin != null
      ? Number(entry.durationMin)
      : (sleepDetails.durationHours != null
        ? Math.round(Number(sleepDetails.durationHours) * 60)
        : (entry.sleepDuration != null ? Math.round(Number(entry.sleepDuration)) : null));
  const durationLabel = durationMin != null ? `${Math.floor(durationMin / 60)}h ${durationMin % 60}m` : '';

  if (!wakeTime || status === 'in_progress' || status === 'incomplete' || status === 'active') {
    return null;
  }

  if (bedtime) {
    return durationLabel
      ? `Slept ${bedtime} → ${wakeTime} · ${durationLabel}`
      : `Slept ${bedtime} → ${wakeTime}`;
  }

  return durationLabel ? `Slept · ${durationLabel}` : 'Sleep logged';
};

const getToiletDetails = (entry = {}, adaptivePresentation = null) => {
  const bathroomDetails = entry.bathroomDetails || entry.data?.bathroomDetails || {};
  const rows = [];
  const consistencyMeta = getBristolMeta(bathroomDetails.consistency || entry.consistency);
  const locationText = normalizeText(bathroomDetails.location || entry.location || '');
  const locationLabel = normalizeText(bathroomDetails.adaptiveLocationLabel || adaptivePresentation?.locationLabel || 'Location');
  const stoolScaleLabel = normalizeText(bathroomDetails.adaptiveStoolScaleLabel || adaptivePresentation?.stoolScaleLabel || 'Bristol');
  const notesLabel = normalizeText(bathroomDetails.adaptiveNotesLabel || adaptivePresentation?.notesLabel || 'Notes');
  const hasStructuredBathroomFields = Boolean(
    normalizeText(bathroomDetails.type || entry.toiletType || '')
    || locationText
    || consistencyMeta
  );
  const narrativeText = normalizeText(
    bathroomDetails.notes
    || entry.notes
    || entry.description
    || entry.summary
    || entry.content
    || entry.text
    || entry.title
    || ''
  );

  if (locationText && locationText.toLowerCase() !== 'school') {
    rows.push({ label: locationLabel, value: locationText });
  }

  if (consistencyMeta) {
    rows.push({ label: stoolScaleLabel, value: consistencyMeta.description });
  }

  if (hasStructuredBathroomFields && narrativeText) {
    rows.push({ label: notesLabel, value: narrativeText });
  }

  return rows;
};

const getBehaviorDetails = (entry = {}, contextSnapshot = {}, adaptivePresentation = null) => {
  const rows = [];
  const triggerSummary = normalizeText(entry.triggerSummary || entry.incidentData?.triggerSummary);
  const triggerNotes = normalizeText(entry.triggerNotes || entry.incidentData?.triggerNotes || '');
  const responsePresentation = adaptivePresentation || resolveAdaptiveResponsePresentation({ entry });
  const triggerSummaryLabel = normalizeText(
    entry.adaptiveTriggerSummaryLabel
    || entry.incidentData?.adaptiveTriggerSummaryLabel
    || responsePresentation.triggerSummaryLabel
    || 'Possible context'
  );
  const triggerNotesLabel = normalizeText(
    entry.adaptiveTriggerNotesLabel
    || entry.incidentData?.adaptiveTriggerNotesLabel
    || responsePresentation.triggerNotesLabel
    || 'Possible trigger'
  );

  if (triggerSummary) {
    rows.push({ label: triggerSummaryLabel, value: triggerSummary });
  }

  if (triggerNotes && triggerNotes !== triggerSummary) {
    rows.push({ label: triggerNotesLabel, value: triggerNotes });
  }

  const contextFlags = buildContextSignalsFromSnapshot(contextSnapshot);

  return {
    rows,
    contextFlags,
  };
};

const getAdaptiveIncidentSeverityLabel = (entry = {}, adaptivePresentation = null) => {
  const severity = Number(entry.severity ?? entry.incidentData?.severity);
  if (Number.isFinite(severity)) {
    const adaptivePresetLabel = normalizeText(
      adaptivePresentation?.severityPresets?.find((preset) => preset.value === severity)?.label
    );
    if (adaptivePresetLabel && adaptivePresentation?.lensKey && adaptivePresentation.lensKey !== 'behavior') {
      return adaptivePresetLabel;
    }
  }

  const savedLabel = normalizeText(entry.severityLabel || entry.incidentData?.severityLabel);
  if (savedLabel) {
    return savedLabel;
  }

  if (!Number.isFinite(severity)) {
    return '';
  }

  return normalizeText(adaptivePresentation?.severityPresets?.find((preset) => preset.value === severity)?.label);
};

const buildAdaptiveIncidentSummary = (entry = {}, adaptivePresentation = null) => {
  const responsePresentation = adaptivePresentation || resolveAdaptiveResponsePresentation({ entry });
  const severityTitle = normalizeText(
    entry.adaptiveSeverityLabel
    || entry.incidentData?.adaptiveSeverityLabel
    || responsePresentation.severityLabel
    || 'Severity'
  );
  const triggerSummaryLabel = normalizeText(
    entry.adaptiveTriggerSummaryLabel
    || entry.incidentData?.adaptiveTriggerSummaryLabel
    || responsePresentation.triggerSummaryLabel
    || 'Possible context'
  );
  const triggerNotesLabel = normalizeText(
    entry.adaptiveTriggerNotesLabel
    || entry.incidentData?.adaptiveTriggerNotesLabel
    || responsePresentation.triggerNotesLabel
    || 'Possible trigger'
  );
  const severityLabel = getAdaptiveIncidentSeverityLabel(entry, responsePresentation);
  const triggerSummary = normalizeText(entry.triggerSummary || entry.incidentData?.triggerSummary || entry.remedy || entry.incidentData?.remedy || '');
  const triggerNotes = normalizeText(entry.triggerNotes || entry.incidentData?.triggerNotes || '');

  return [
    severityLabel ? `${severityTitle}: ${severityLabel}` : null,
    triggerNotes ? `${triggerNotesLabel}: ${triggerNotes}` : null,
    triggerSummary ? `${triggerSummaryLabel}: ${triggerSummary}` : null,
  ].filter(Boolean).join(' • ');
};

const isGeneratedIncidentSummaryText = (value = '') => (
  /^\s*(severity|comfort level|support level)\s*:/i.test(normalizeText(value))
);

const isGenericIncidentNoteTitle = (value = '') => (
  /^(behavior|comfort|symptoms?|mood & behavior|response)\s+note$/i.test(normalizeText(value))
);

const formatMedicationChangeValue = (value) => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return normalizeText(value);
  }

  if (typeof value !== 'object') {
    return normalizeText(value);
  }

  const label = normalizeText(value.displayLabel || value.label || value.name);
  const doseText = normalizeText(
    value.dose && value.unit ? `${value.dose} ${value.unit}` : (value.dose || value.unit || '')
  );
  const timeText = normalizeText(value.time || value.scheduledTime || '');

  if (label && doseText && timeText) {
    return `${label}: ${doseText} at ${timeText}`;
  }

  if (label && doseText) {
    return `${label}: ${doseText}`;
  }

  if (label && timeText) {
    return `${label}: ${timeText}`;
  }

  if (doseText && timeText) {
    return `${doseText} at ${timeText}`;
  }

  return label || doseText || timeText || normalizeText(value.from || value.to || value.scheduledDose || value.scheduledUnit || value.scheduledTime);
};

const formatMedicationChangeDateLabel = (value) => {
  const date = coerceCalendarDate(value);
  if (!date) {
    return normalizeText(value);
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};


const hasStructuredMedicationPresentation = (entry = {}) => Boolean(
  entry.medicationLogState
  || entry.actualDose
  || entry.givenDose
  || entry.plannedDose
  || entry.medicationScheduleType
  || entry.medicationEffectLabel
  || entry.medicationAsNeededReason
  || entry.medicationCourseDayLabel
  || entry.medicationVersionSnapshot
  || entry.medicationScheduleSnapshot
  || entry.takenAt
  || entry.actualDateTime
);

const getMedicationChangeDetails = (entry = {}) => {
  const changeItems = Array.isArray(entry.medicationChangeDetails)
    ? entry.medicationChangeDetails
    : (Array.isArray(entry.changes) ? entry.changes : []);
  const rows = [];

  changeItems.forEach((change) => {
    const fieldLabel = normalizeText(change?.label || change?.field || 'Change');
    const fromValue = formatMedicationChangeValue(change?.from);
    const toValue = formatMedicationChangeValue(change?.to);

    if (!fromValue && !toValue) {
      return;
    }

    rows.push({
      label: fieldLabel,
      value: fromValue && toValue ? `${fromValue} → ${toValue}` : (toValue || fromValue),
    });
  });

  const effectiveLabel = normalizeText(
    entry.medicationChangeEffectiveLabel
    || entry.effectiveLabel
    || formatMedicationChangeDateLabel(entry.effectiveFrom || entry.startDate || entry.actualDateTime)
  );

  if (effectiveLabel) {
    rows.push({
      label: 'Effective',
      value: effectiveLabel,
    });
  }

  return rows;
};

const getMedicationRoundPresentation = (entry = {}) => {
  const items = Array.isArray(entry.medicationRoundItems) ? entry.medicationRoundItems : [];
  const counts = items.length
    ? items.reduce((result, item) => ({
      ...result,
      [item.status]: (result[item.status] || 0) + 1,
    }), {})
    : { ...(entry.administrationCounts || {}) };
  const total = Number(counts.taken || 0) + Number(counts.missed || 0) + Number(counts.not_taken || 0);
  const allTaken = total > 0 && Number(counts.taken || 0) === total;
  const scheduledTime = normalizeText(entry.scheduledTime);
  const hour = /^\d{2}:\d{2}$/.test(scheduledTime) ? Number(scheduledTime.slice(0, 2)) : null;
  const period = Number.isFinite(hour)
    ? (hour < 11 ? 'Morning' : hour < 15 ? 'Midday' : hour < 21 ? 'Evening' : 'Bedtime')
    : 'Medication';
  const storedRoundName = normalizeText(entry.medicationRoundName).replace(/\s+round$/i, '');
  const roundLabel = storedRoundName || period;
  const summary = allTaken
    ? `${total} medication${total === 1 ? '' : 's'} taken`
    : [counts.taken ? `${counts.taken} taken` : '', counts.missed ? `${counts.missed} missed` : '', counts.not_taken ? `${counts.not_taken} not taken` : ''].filter(Boolean).join(' · ');

  return {
    title: `${roundLabel} round ${allTaken ? 'completed' : 'recorded'}`,
    summary,
  };
};

export const transformTimelineEntry = (entry = {}, presentation = {}, timeString = '', child = null) => {
  const sleepStatus = normalizeText(entry.sleepDetails?.status || entry.status).toLowerCase();
  const behaviorType = entry.behaviorType || getTimelineEntryBehaviorType(entry);
  const editTarget = entry.editTarget || getTimelineEntryEditTarget(entry);
  if (
    entry.category === 'sleep'
    && hasFullStructuredSleepPresentation(entry)
    && (sleepStatus === 'active' || sleepStatus === 'in_progress' || sleepStatus === 'incomplete')
  ) {
    return null;
  }

  if (isStoryMomentReferenceEntry(entry)) {
    const referencePresentation = buildStoryMomentReferencePresentation(entry);
    return {
      type: entry.collection || entry.type || 'log',
      kind: referencePresentation.kind,
      presentation,
      behaviorType: '',
      editTarget: '',
      parsedObservation: false,
      source: null,
      sourceText: '',
      originalText: '',
      label: referencePresentation.label,
      displayTitle: referencePresentation.displayTitle,
      bodyText: referencePresentation.bodyText,
      categoryLabel: referencePresentation.categoryLabel,
      icon: referencePresentation.icon,
      iconComponent: referencePresentation.iconComponent,
      time: timeString,
      subtype: '',
      primaryText: referencePresentation.primaryText,
      insight: '',
      triggerSummary: referencePresentation.triggerSummary,
      notesText: referencePresentation.notesText,
      contextFlags: [],
      initials: '',
      caregiverId: '',
      caregiverPalette: null,
      caregiverRole: null,
      caregiverDisplayName: '',
      caregiverRoleDisplay: null,
      sourceMeta: referencePresentation.sourceMeta,
      captureFirstDetails: null,
      timestampValue: entry.timestamp || entry.createdAt || entry.entryDate || null,
      mediaURL: null,
      mediaUrls: [],
      mediaAttachments: [],
      accentColor: LOG_TYPES.log.palette.dot,
      metaBadge: null,
      severityMeta: null,
      severityLevel: null,
      detailRows: [],
      foodReviewFlag: false,
      foodReviewMessage: '',
      activityTheme: null,
      categoryPalette: LOG_TYPES.log.palette,
      categoryDisplay: getCanonicalEntryDisplayInfo({ category: 'log' }),
      navigationTarget: referencePresentation.navigationTarget,
      sourceId: referencePresentation.sourceId,
      pointerKey: referencePresentation.pointerKey,
      count: referencePresentation.count,
      candidateRefs: referencePresentation.candidateRefs,
      sourcePresentation: referencePresentation.sourcePresentation,
    };
  }

  if (isDocumentPointerEntry(entry)) {
    const pointerPresentation = buildDocumentPointerPresentation(entry);
    return {
      type: entry.collection || entry.type || 'log',
      kind: pointerPresentation.kind,
      presentation,
      behaviorType: '',
      editTarget: '',
      parsedObservation: false,
      source: null,
      sourceText: '',
      originalText: '',
      label: pointerPresentation.label,
      displayTitle: pointerPresentation.displayTitle,
      bodyText: pointerPresentation.bodyText,
      categoryLabel: pointerPresentation.categoryLabel,
      icon: '📄',
      iconComponent: null,
      time: timeString,
      subtype: '',
      primaryText: '',
      insight: '',
      triggerSummary: '',
      notesText: pointerPresentation.notesText,
      contextFlags: [],
      initials: '',
      caregiverId: '',
      caregiverPalette: null,
      caregiverRole: null,
      caregiverDisplayName: '',
      caregiverRoleDisplay: null,
      sourceMeta: pointerPresentation.sourceMeta,
      captureFirstDetails: null,
      timestampValue: entry.timestamp || entry.createdAt || entry.entryDate || null,
      mediaURL: null,
      mediaUrls: [],
      mediaAttachments: [],
      accentColor: LOG_TYPES.log.palette.dot,
      metaBadge: null,
      severityMeta: null,
      severityLevel: null,
      detailRows: [],
      foodReviewFlag: false,
      foodReviewMessage: '',
      activityTheme: null,
      categoryPalette: LOG_TYPES.log.palette,
      categoryDisplay: getCanonicalEntryDisplayInfo({ category: 'log' }),
      navigationTarget: pointerPresentation.navigationTarget,
      sourceId: pointerPresentation.sourceId,
      pointerKey: pointerPresentation.pointerKey,
      count: pointerPresentation.count,
      candidateRefs: pointerPresentation.candidateRefs,
      sourcePresentation: pointerPresentation.sourcePresentation,
    };
  }

  const categoryDisplay = getCanonicalEntryDisplayInfo(entry);
  const captureFirstDetails = buildCaptureFirstTimelineDetails(entry);
  const categoryPalette = categoryDisplay.palette || LOG_TYPES.log.palette;
  const isBehaviorIncident = isBehaviorIncidentEntry(entry);
  const adaptiveIncidentPresentation = isBehaviorIncident
    ? resolveAdaptiveResponsePresentation({ entry, child })
    : null;
  const adaptiveIncidentLabel = normalizeText(adaptiveIncidentPresentation?.categoryLabel || '');
  const adaptiveIncidentNoteTitle = normalizeText(adaptiveIncidentPresentation?.title || '');
  const adaptiveIncidentTitle = isBehaviorIncident
    ? (adaptiveIncidentNoteTitle || adaptiveIncidentLabel || LOG_TYPES.behavior.displayLabel)
    : '';
  const isActivityEntry = entry.category === 'activity' || entry.logCategory === 'activity';
  const isMoodEntry = entry.type === 'mood'
    || entry.timelineType === 'mood'
    || entry.category === 'mood'
    || entry.actionType === 'mood'
    || entry.logCategory === 'mood';
  const moodValue = normalizeText(
    entry.moodValue
    || entry.value
    || entry.data?.level
    || entry.content
    || entry.text
    || entry.notes
    || 'Calm'
  );
  const contextSnapshot = entry.contextSnapshot || entry.incidentData?.contextSnapshot || null;
  const activityMeta = isActivityEntry ? resolveActivityMeta(entry) : null;
  const severityMeta = getSemanticSeverityMeta(entry.severity ?? entry.incidentData?.severity);
  const engagementMeta = isActivityEntry
    ? getEngagementMeta(entry.activityDetails?.engagement ?? entry.engagement ?? entry.data?.engagement ?? entry.value)
    : null;
  const kind = isBehaviorIncident
    ? 'behavior'
    : isMoodEntry
      ? 'mood'
    : isActivityEntry
      ? 'activity'
      : isDoctorVisitEntry(entry)
        ? 'doctor_visit'
        : entry.category === 'food'
          ? 'food'
          : entry.category === 'medication'
            ? 'meds'
            : entry.category === 'medication_change'
              ? 'medication_change'
            : entry.category === 'sleep'
              ? 'sleep'
            : (entry.category === 'bathroom' || entry.category === 'toileting')
                ? 'toilet'
                : 'generic';
  const isMedicationRoundEntry = kind === 'meds' && (
    entry.type === 'medication_round'
    || entry.provenance?.kind === 'medication_reminder_round'
    || Array.isArray(entry.medicationRoundItems)
  );
  const adaptiveFoodPresentation = kind === 'food'
    ? resolveAdaptiveFoodLogPresentation({ entry, child })
    : null;
  const adaptiveBathroomPresentation = kind === 'toilet'
    ? resolveAdaptiveBathroomLogPresentation({ entry, child })
    : null;
  const isImportedEntry = isImportSourceEntry(entry);
  const isSmartCaptureObservation = isSmartCaptureObservationEntry(entry);
  const isSmartCaptureMoodNote = kind === 'mood'
    && behaviorType === 'narrative_note'
    && isSmartCaptureObservation;
  const isSmartCaptureSleepNote = kind === 'sleep'
    && behaviorType === 'narrative_note'
    && !hasFullStructuredSleepPresentation(entry)
    && (isSmartCaptureObservation || isQuickLogSleepNoteEntry(entry));
  const importedNarrativeText = getImportedNarrativeText(entry);
  const quickLogSleepNarrativeText = normalizeText(entry.notes || entry.description || entry.summary || entry.content || entry.text || entry.title || '');
  const shouldUseImportFoodNarrative = isImportedEntry && kind === 'food' && !hasRichImportedFoodStructure(entry);
  const shouldUseImportSleepNarrative = isImportedEntry && kind === 'sleep' && !hasFullStructuredSleepPresentation(entry);
  const shouldUseImportMedicationNarrative = isImportedEntry && kind === 'meds' && !hasStructuredMedicationPresentation(entry);
  const shouldUseQuickLogSleepNarrative = kind === 'sleep'
    && isQuickLogSleepNoteEntry(entry)
    && !hasFullStructuredSleepPresentation(entry);

  const label = kind === 'doctor_visit'
    ? 'Doctor visit'
    : (categoryDisplay.label || 'Log');
  const noteStyleLabel = isSmartCaptureMoodNote
    ? 'Mood note'
    : (isSmartCaptureSleepNote ? 'Sleep note' : '');

  const icon = isBehaviorIncident
    ? (LOG_TYPES.behavior.iconComponent ? null : LOG_TYPES.behavior.icon)
    : isMoodEntry
      ? (entry.moodIconComponent || entry.categoryIconComponent || LOG_TYPES.mood.iconComponent
        ? (entry.moodEmoji || entry.categoryIcon || null)
        : (entry.moodEmoji || entry.categoryIcon || LOG_TYPES.mood.icon))
    : isActivityEntry
      ? (activityMeta?.iconComponent ? null : (activityMeta?.icon || LOG_TYPES.activity.icon))
      : kind === 'doctor_visit'
        ? '🏥'
        : (presentation.entryIconComponent || categoryDisplay.iconComponent ? (presentation.entryIcon || categoryDisplay.icon || null) : (presentation.entryIcon || categoryDisplay.icon || '•'));
  const iconComponent = isBehaviorIncident
    ? (LOG_TYPES.behavior.iconComponent || null)
    : isMoodEntry
      ? (entry.moodIconComponent || entry.categoryIconComponent || LOG_TYPES.mood.iconComponent || null)
      : isActivityEntry
        ? (activityMeta?.iconComponent || null)
        : kind === 'doctor_visit'
          ? null
          : (presentation.entryIconComponent || categoryDisplay.iconComponent || null);

  const accentColor = isMoodEntry
    ? (categoryDisplay.palette?.dot || categoryPalette?.dot || LOG_TYPES.mood.palette.dot)
    : categoryDisplay.palette?.dot
    || categoryPalette?.dot
    || (isActivityEntry ? activityMeta?.color : null)
    || (entry.category === 'sleep' ? LOG_TYPES.sleep.palette.dot : LOG_TYPES.log.palette.dot);

  const primaryText = isBehaviorIncident
    ? ''
    : isMoodEntry
      ? (isSmartCaptureMoodNote
        ? normalizeText(
            entry.notes
            || entry.description
            || entry.summary
            || entry.content
            || entry.text
            || entry.title
            || moodValue
          )
        : moodValue)
    : isActivityEntry
      ? normalizeText(entry.activityDetails?.notes || entry.data?.notes || entry.notes || entry.description || entry.content || entry.text || '')
      : kind === 'food'
        ? (() => {
            if (shouldUseImportFoodNarrative) {
              return importedNarrativeText;
            }

            const mealType = normalizeText(getFoodMealType(entry));
            const headline = normalizeText(
              getFoodTextHeadline(getFoodHeadline(entry))
            );
            if (mealType && headline) return `${mealType} — ${headline}`;
            return headline || mealType || adaptiveFoodPresentation?.fallbackText || '';
          })()
          : kind === 'meds'
            ? (shouldUseImportMedicationNarrative
              ? importedNarrativeText
              : normalizeText(
                entry.medicationVersionSnapshot?.name
                || entry.medicationScheduleSnapshot?.medicationName
                || entry.medicationName
                || entry.medicationDetails?.medicationName
                || entry.medicationDetails?.name
                || entry.title
                || entry.categoryLabel
                || entry.titlePrefix
                || 'Medication'
              ))
            : kind === 'medication_change'
              ? normalizeText(
                  entry.medicationName
                  || entry.title
                  || entry.medicationChangeSummary
                  || entry.summary
                  || ''
                )
            : kind === 'doctor_visit'
              ? normalizeText([
                  entry.providerName || entry.doctorName || entry.providerSnapshot?.doctorName || '',
                  entry.providerSpecialty || entry.specialty || entry.providerSnapshot?.specialty || '',
                ].filter(Boolean).join(' · ')) || normalizeText(
                  entry.reasonForVisit
                  || entry.title
                  || 'Doctor visit'
                )
            : kind === 'sleep'
            ? (shouldUseImportSleepNarrative
              ? importedNarrativeText
              : isSmartCaptureSleepNote
                ? (isImportedEntry ? importedNarrativeText : quickLogSleepNarrativeText)
                : shouldUseQuickLogSleepNarrative
                  ? quickLogSleepNarrativeText
                  : normalizeText(getSleepHeadline(entry)))
            : kind === 'toilet'
              ? normalizeText(getToiletSummary(entry) || adaptiveBathroomPresentation?.fallbackText || '')
              : normalizeText(entry.notes || entry.description || entry.summary || entry.content || entry.text || entry.title || '');

  const subtype = '';

  const insight = normalizeText(
    contextSnapshot?.patternInsight
      || entry.patternInsight
      || ''
  );
  const triggerSummary = normalizeText(entry.triggerSummary || entry.incidentData?.triggerSummary || '');
  const initials = getInitials(entry.loggedByUser || entry.authorName || entry.authorEmail || entry.author || '');
  const caregiverId = entry.loggedBy?.id || entry.authorId || entry.createdBy || entry.userId || entry.loggedByUser || entry.authorName || entry.authorEmail || entry.author || '';
  const caregiverPalette = getCaregiverPalette(caregiverId);
  const caregiverRole = entry.loggedBy?.role || entry.authorRole || entry.userRole || entry.loggedByRole || null;
  const caregiverDisplayName = normalizeText(
    entry.loggedBy?.name
    || entry.loggedByUser
    || entry.authorName
    || entry.authorEmail
    || entry.author
    || ''
  );
  const caregiverRoleDisplay = caregiverRole ? getRoleDisplay(caregiverRole) : null;
  const sourceMeta = getTimelineSourceMeta(entry.source || entry, {
    senderName: caregiverDisplayName || entry.authorName || entry.loggedByUser || '',
    senderEmail: entry.authorEmail || entry.loggedByEmail || '',
    subject: entry.subject || entry.title || '',
    body: entry.text || entry.content || entry.note || entry.description || '',
    receivedAt: entry.timestamp || entry.createdAt || null,
    messageId: entry.sourceMessageId || entry.incomingMessageId || null,
    extractedBy: entry.source?.extractedBy || entry.extractedBy || null,
    confidence: entry.source?.confidence || entry.confidence || null,
  });

  const metaBadge = isBehaviorIncident
    ? (severityMeta
      ? {
          label: severityMeta.displayLabel,
          color: severityMeta.color,
          textColor: severityMeta.textColor,
          bg: severityMeta.bg,
          border: severityMeta.border,
          icon: severityMeta.value >= 7 ? '⚠️' : null,
        }
      : null)
    : isActivityEntry
      ? (engagementMeta
        ? {
            label: engagementMeta.label,
            color: activityMeta?.color || LOG_TYPES.activity.palette.dot,
            textColor: engagementMeta.textColor,
            bg: engagementMeta.bg,
            border: activityMeta?.border || 'rgba(99, 102, 241, 0.18)',
          }
        : null)
      : (severityMeta
      ? {
            label: severityMeta.displayLabel,
            color: severityMeta.color,
            textColor: severityMeta.textColor,
            bg: severityMeta.bg,
            border: severityMeta.border,
            icon: severityMeta.value >= 7 ? '⚠️' : null,
          }
        : null);

  const behaviorDetails = isBehaviorIncident ? getBehaviorDetails(entry, contextSnapshot || {}, adaptiveIncidentPresentation) : null;
  const behaviorTitle = isBehaviorIncident ? adaptiveIncidentTitle : '';

  const detailRows = isBehaviorIncident
    ? (behaviorDetails?.rows || [])
    : kind === 'activity'
      ? (() => {
          const rows = [];
          const typeLabel = normalizeText(activityMeta?.label || entry.activityDetails?.activityThemeLabel || entry.activityThemeLabel || entry.data?.activityThemeLabel || '');
          if (typeLabel) rows.push({ label: 'Type', value: typeLabel });
          const durationMin = entry.activityDetails?.activityTiming?.durationMinutes ?? entry.durationMin ?? entry.data?.durationMin;
          const durationText = durationMin != null
            ? formatSleepDuration(durationMin)
            : normalizeText(entry.activityDetails?.duration || entry.duration || entry.data?.duration || '');
          if (durationText) rows.push({ label: 'Duration', value: durationText });
          if (engagementMeta?.label) rows.push({ label: 'Engagement', value: engagementMeta.label });
          return rows;
        })()
        : kind === 'doctor_visit'
          ? getDoctorVisitDetails(entry)
        : kind === 'food'
        ? (shouldUseImportFoodNarrative ? [] : getFoodDetails(entry, adaptiveFoodPresentation))
        : kind === 'meds'
          ? (isMedicationRoundEntry
            ? (entry.medicationRoundItems || []).map((item) => {
                const actualTime = coerceCalendarDate(item.actualTakenAt)?.toLocaleTimeString?.([], { hour: 'numeric', minute: '2-digit' }) || '';
                const details = [item.dose, item.status === 'taken' && actualTime ? `taken ${actualTime}` : '', item.reason, item.note].filter(Boolean).join(' · ');
                return {
                  label: item.status === 'taken' ? 'Taken' : item.status === 'missed' ? 'Missed' : 'Not taken',
                  value: `${item.medicationName || 'Medication'}${details ? ` · ${details}` : ''}`,
                };
              }).concat(entry.revisions?.length ? [{ label: 'History', value: `Corrected ${entry.revisions.length} time${entry.revisions.length === 1 ? '' : 's'}` }] : [])
            : (shouldUseImportMedicationNarrative ? [] : buildMedicationLogRows(entry)))
          : kind === 'medication_change'
              ? getMedicationChangeDetails(entry)
        : kind === 'sleep'
            ? ((shouldUseImportSleepNarrative || shouldUseQuickLogSleepNarrative || isSmartCaptureSleepNote) ? [] : getSleepDetails(entry))
            : kind === 'toilet'
              ? getToiletDetails(entry, adaptiveBathroomPresentation)
              : [];
  const foodReviewFlag = kind === 'food' ? getFoodReviewFlag(entry) : false;
  const foodReviewMessage = foodReviewFlag
    ? 'This log will be marked for caregiver review'
    : '';
  const medicationRoundPresentation = isMedicationRoundEntry
    ? getMedicationRoundPresentation(entry)
    : null;

  const contextFlags = isBehaviorIncident ? behaviorDetails.contextFlags : [];
  const adaptiveIncidentSummary = isBehaviorIncident ? buildAdaptiveIncidentSummary(entry, adaptiveIncidentPresentation) : '';
  const incidentDescriptionText = normalizeText(
    entry.incidentData?.description
    || entry.description
    || entry.notes
    || entry.text
    || entry.title
    || ''
  );
  const incidentContentText = normalizeText(entry.content || '');
  const bodyText = medicationRoundPresentation?.summary || normalizeText(
    isBehaviorIncident
      ? (
        isGeneratedIncidentSummaryText(incidentDescriptionText)
          || isGenericIncidentNoteTitle(incidentDescriptionText)
          || !incidentDescriptionText
          ? (adaptiveIncidentSummary || incidentContentText)
          : incidentDescriptionText
      )
      : (
        entry.body
        || entry.content
        || entry.notes
        || entry.description
        || entry.summary
        || entry.text
        || entry.title
        || primaryText
        || ''
      )
  );
  const previewTitle = resolvePreviewTitle({
    ...entry,
    body: bodyText,
    title: entry.title || entry.titlePrefix || '',
    titlePrefix: entry.titlePrefix || '',
    parsedSourceCategory: kind === 'meds' ? 'medication' : kind,
    category: kind === 'meds' ? 'medication' : kind,
  });
  const adaptiveStructuredTitle = kind === 'food'
    ? adaptiveFoodPresentation?.title
    : kind === 'toilet'
      ? adaptiveBathroomPresentation?.title
      : '';
  const resolvedDisplayTitle = isBehaviorIncident
    ? resolveCaptureFirstDisplayTitle({
        displayTitle: captureFirstDetails.enabled ? (entry.title || adaptiveIncidentTitle) : adaptiveIncidentTitle,
        bodyText, category: 'behavior', captureFirstDetails,
      })
    : adaptiveStructuredTitle || resolveCaptureFirstDisplayTitle({
        displayTitle: previewTitle,
        bodyText,
        category: kind === 'meds' ? 'medication' : kind,
        captureFirstDetails,
      });
  const displayTitle = medicationRoundPresentation?.title || (/^original note$/i.test(normalizeText(resolvedDisplayTitle))
    ? resolveTitle(entry, 'timeline-unsplit')
    : resolvedDisplayTitle);

  return {
    type: entry.collection || entry.type || 'log',
    kind,
    presentation,
    behaviorType,
    editTarget,
    parsedObservation: Boolean(entry.parsedObservation || entry.source?.parsedObservation || entry.originalData?.parsedObservation),
    source: entry.source || null,
    sourceText: entry.sourceText || entry.originalText || '',
    originalText: entry.originalText || entry.sourceText || '',
    label: kind === 'meds'
      ? 'Medications'
      : isBehaviorIncident
      ? (adaptiveIncidentLabel || behaviorTitle || LOG_TYPES.behavior.displayLabel)
      : kind === 'food'
        ? (adaptiveFoodPresentation?.categoryLabel || label)
      : kind === 'toilet'
        ? (adaptiveBathroomPresentation?.categoryLabel || label)
      : (kind === 'doctor_visit' ? 'Doctor visit' : (noteStyleLabel || (isMoodEntry ? LOG_TYPES.mood.displayLabel : label))),
    displayTitle,
    bodyText,
    categoryLabel: kind === 'meds'
      ? 'Medications'
      : noteStyleLabel
      || (isBehaviorIncident
        ? (adaptiveIncidentLabel || categoryDisplay.label || categoryDisplay.titlePrefix || entry.categoryLabel || label)
        : kind === 'food'
          ? (adaptiveFoodPresentation?.categoryLabel || categoryDisplay.label || label)
        : kind === 'toilet'
          ? (adaptiveBathroomPresentation?.categoryLabel || categoryDisplay.label || label)
        : (kind === 'doctor_visit' ? 'Doctor visit' : (isMoodEntry ? LOG_TYPES.mood.displayLabel : (categoryDisplay.label || categoryDisplay.titlePrefix || entry.categoryLabel || label)))),
    icon,
    iconComponent,
    time: timeString,
    subtype,
    primaryText: isBehaviorIncident ? '' : primaryText,
    insight,
    triggerSummary: isBehaviorIncident ? triggerSummary : triggerSummary,
    notesText: isBehaviorIncident
      ? ''
      : isActivityEntry
        ? normalizeText(entry.activityDetails?.notes || entry.notes || '')
        : normalizeText(entry.notes || entry.incidentData?.notes || ''),
    contextFlags,
    initials,
    caregiverId,
    caregiverPalette,
    caregiverRole,
    caregiverDisplayName,
    caregiverRoleDisplay,
    sourceMeta,
    captureFirstDetails,
    timestampValue: entry.timestamp || entry.createdAt || entry.date || null,
    mediaURL: entry.mediaURL || entry.mediaUrl || null,
    mediaUrls: (() => {
      // Prefer canonical media fields, while retaining compatibility with
      // capture-first records created before attachment normalization.
      if (Array.isArray(entry.mediaUrls) && entry.mediaUrls.length) return entry.mediaUrls;
      if (Array.isArray(entry.imageAttachments) && entry.imageAttachments.length) return entry.imageAttachments;
      const fromSource = Array.isArray(entry.source?.imageAttachments) ? entry.source.imageAttachments : [];
      if (fromSource.length) return fromSource;
      const fromMetadata = Array.isArray(entry.metadata?.source?.imageAttachments)
        ? entry.metadata.source.imageAttachments
        : [];
      if (fromMetadata.length) return fromMetadata;
      if (Array.isArray(entry.source?.mediaUrls) && entry.source.mediaUrls.length) return entry.source.mediaUrls;
      return Array.isArray(entry.metadata?.source?.mediaUrls) ? entry.metadata.source.mediaUrls : [];
    })(),
    mediaAttachments: Array.isArray(entry.mediaAttachments) ? entry.mediaAttachments : [],
    audioAttachments: Array.isArray(entry.audioAttachments) && entry.audioAttachments.length
      ? entry.audioAttachments
      : (Array.isArray(entry.source?.audioAttachments) && entry.source.audioAttachments.length
        ? entry.source.audioAttachments
        : (Array.isArray(entry.metadata?.source?.audioAttachments) ? entry.metadata.source.audioAttachments : [])),
    accentColor,
    metaBadge,
    severityMeta,
    severityLevel: severityMeta?.value ?? (Number.isFinite(Number(entry.severity ?? entry.incidentData?.severity)) ? Number(entry.severity ?? entry.incidentData?.severity) : null),
    detailRows,
    foodReviewFlag,
    foodReviewMessage,
    activityTheme: activityMeta,
    categoryPalette,
    categoryDisplay,
    attributionVerb: medicationRoundPresentation ? 'Recorded by' : 'Logged by',
  };
};

export {
  getSemanticSeverityMeta,
  getEngagementMeta,
  getBehaviorContextSignals,
  getBehaviorDetails,
  resolveActivityMeta,
};
